import SecureLS from "secure-ls";

const ENCRYPTION_KEY = import.meta.env.VITE_ENCRYPTION_KEY as string;
if (!ENCRYPTION_KEY) {
  throw new Error("[storage] VITE_ENCRYPTION_KEY env variable is not set.");
}

/**
 * Secure localStorage wrapper using secure-ls.
 * All data is AES-encrypted before being written to localStorage.
 */
const secureStorage = new SecureLS({
  encodingType: "aes",
  encryptionSecret: ENCRYPTION_KEY,
  isCompression: false,
});

/**
 * Get a value from secure storage
 * @param key - Storage key
 * @returns Stored value or null
 */
export const getItem = <T = string>(key: string): T | null => {
  try {
    const value = secureStorage.get(key);
    if (value === undefined || value === "") {
      return null;
    }
    return value as T;
  } catch {
    return null;
  }
};

/**
 * Set a value in secure storage
 * @param key - Storage key
 * @param value - Value to store (string or object — auto-serialized)
 */
export const setItem = <T = string>(key: string, value: T): void => {
  try {
    secureStorage.set(key, value);
  } catch {
    // Storage failure — silently ignored
  }
};

/**
 * Remove a value from secure storage
 * @param key - Storage key
 */
export const removeItem = (key: string): void => {
  try {
    secureStorage.remove(key);
  } catch {
    // Removal failure — silently ignored
  }
};

/**
 * Clear all secure storage data
 */
export const clearAll = (): void => {
  try {
    secureStorage.removeAll();
  } catch {
    // Clear failure — silently ignored
  }
};

export default secureStorage;
